import type { Room } from '@brush/api-client';

/**
 * Which room the dialog is about — <b>an id and a mode, never the room itself.</b>
 *
 * <p>
 * The table used to hold <code>Room | null | undefined</code>, where null meant <em>creating</em>
 * and undefined meant <em>closed</em>. That shape cannot say <em>gone</em>, and what it costs is
 * the fantasy walk's finding F2: a room deleted while its dialog is open falls back to the null
 * arm, the dialog retitles itself to "Register new poker room" under somebody mid-edit, and their
 * next Save creates a second room rather than failing.
 * </p>
 */
export type RoomDialogTarget =
  | { mode: 'closed' }
  | { mode: 'creating' }
  | { mode: 'editing'; id: string };

export type RoomDialogState =
  | { kind: 'closed' }
  | { kind: 'creating' }
  | { kind: 'editing'; room: Room }
  | { kind: 'gone' };

/**
 * The target resolved against the live list.
 *
 * <p>
 * <b>Re-resolved on every render rather than remembered</b>, which is the shape
 * <code>LeaderboardsView</code> settled on for the same reason: holding the object means the dialog
 * reads a snapshot taken when it opened, so a save that moves the room leaves the screen showing
 * what it used to be. A successful mutation that does not visibly land is what invites the second
 * click.
 * </p>
 * <p>
 * <b>There is no seed for the gap after a create, because there is no gap.</b>
 * <code>useCreateRoom</code> writes the room the server returned into the list's cache, so the
 * list carries it before this is asked — which is a fact the server has just stated rather than
 * anything invented. The alternative, carrying the created room here until the refetch lands,
 * needs a one-way latch to retire it: kept a moment too long it makes a later deletion
 * unnoticeable, which is the first case wearing the third's clothes.
 * </p>
 */
export const roomDialogState = (
  target: RoomDialogTarget,
  rooms: readonly Room[],
): RoomDialogState => {
  if (target.mode === 'closed') return { kind: 'closed' };
  if (target.mode === 'creating') return { kind: 'creating' };

  const room = rooms.find((r) => r.id === target.id);

  return room ? { kind: 'editing', room } : { kind: 'gone' };
};
